import { useCallback, useEffect, useState } from "react";
import { DEFAULT_CONTACT_LIST_PAGE_SIZE, type ContactListItem } from "@multi-chat/shared";
import { listContacts } from "../../api/contacts-api.js";

export const CONTACT_SEARCH_MIN_LENGTH = 2;
const CONTACT_SEARCH_DEBOUNCE_MS = 300;

export type ContactSearchState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "ready"; results: ContactListItem[] }
  | { kind: "error" };

type SettledState = Extract<ContactSearchState, { kind: "ready" | "error" }>;

/** Debounced saved-contact search (existing Contacts list API). Below two characters it stays idle. */
export function useContactSearch(
  organizationId: number,
  search: string,
  excludeContactId?: number,
): { state: ContactSearchState; retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<{ key: string; state: SettledState } | null>(null);
  const query = search.trim();
  const key =
    query.length >= CONTACT_SEARCH_MIN_LENGTH
      ? `${organizationId}:${excludeContactId ?? ""}:${attempt}:${query}`
      : null;

  useEffect(() => {
    if (key == null) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      listContacts(
        organizationId,
        { search: query, page: 1, pageSize: DEFAULT_CONTACT_LIST_PAGE_SIZE },
        controller.signal,
      ).then(
        (response) => {
          if (controller.signal.aborted) return;
          const results = response.items.filter((item) =>
            excludeContactId == null ? true : Number(item.id) !== excludeContactId,
          );
          setSettled({ key, state: { kind: "ready", results } });
        },
        () => {
          if (!controller.signal.aborted) setSettled({ key, state: { kind: "error" } });
        },
      );
    }, CONTACT_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [key, organizationId, query, excludeContactId]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const state: ContactSearchState =
    key == null ? { kind: "idle" } : settled?.key === key ? settled.state : { kind: "searching" };
  return { state, retry };
}
